/**
 * @fileoverview Property Details Form - Trust-Building Verification Display
 * @module PropertyDetailsForm
 * 
 * Business Context:
 * - Step 2 in conversion funnel - REPLACES address input
 * - Displays property verification card with official data from Texas records
 * - Builds trust by showing we accurately retrieved their property data
 * - Single horizontal row with all options visible
 * 
 * Conversion Optimization:
 * - "Property Verified" card creates trust moment
 * - Staggered animations add delight
 * - Shows property ID, lot size for credibility
 * - Pill buttons = single click to select
 * - Elegant transition replaces previous step
 */

import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, Home, DollarSign, Calendar, MapPin, FileText, Ruler } from 'lucide-react';

// Parcel data interface matching backend response
interface ParcelData {
  propId: string;
  geoId: string | null;
  county: string;
  fips: string;
  situsAddr: string;
  situsCity: string;
  situsZip: string;
  mktValue: number | null;
  landValue: number | null;
  impValue: number | null;
  taxYear: number | null;
  yearBuilt: number | null;
  legalArea: number | null;
  legalAreaUnit: string | null;
  landUseCode: string | null;
}

export interface PropertyValueRange {
  id: string;
  label: string;
  shortLabel: string;
  minValue: number;
  maxValue: number;
  midpoint: number;
}

export interface HomeAgeRange {
  id: string;
  label: string;
  shortLabel: string;
  minYears: number;
  maxYears: number;
  savingsBonus: number;
}

// 5 clean value ranges
export const PROPERTY_VALUE_RANGES: PropertyValueRange[] = [
  { id: 'under-250k', label: 'Under $250K', shortLabel: '<$250K', minValue: 100000, maxValue: 250000, midpoint: 175000 },
  { id: '250k-500k', label: '$250-500K', shortLabel: '$250-500K', minValue: 250000, maxValue: 500000, midpoint: 375000 },
  { id: '500k-750k', label: '$500-750K', shortLabel: '$500-750K', minValue: 500000, maxValue: 750000, midpoint: 625000 },
  { id: '750k-1m', label: '$750K-1M', shortLabel: '$750K-1M', minValue: 750000, maxValue: 1000000, midpoint: 875000 },
  { id: 'over-1m', label: '$1M+', shortLabel: '$1M+', minValue: 1000000, maxValue: 3000000, midpoint: 1750000 },
];

// 3 simple age ranges
export const HOME_AGE_RANGES: HomeAgeRange[] = [
  { id: 'new', label: 'Under 5 yrs', shortLabel: '<5 yrs', minYears: 0, maxYears: 5, savingsBonus: 0 },
  { id: 'mid', label: '5-20 yrs', shortLabel: '5-20 yrs', minYears: 5, maxYears: 20, savingsBonus: 0.04 },
  { id: 'older', label: '20+ yrs', shortLabel: '20+ yrs', minYears: 20, maxYears: 100, savingsBonus: 0.08 },
];

export interface PropertyDetails {
  valueRange: PropertyValueRange | null;
  ageRange: HomeAgeRange | null;
}

interface PropertyDetailsFormProps {
  onDetailsChange: (details: PropertyDetails) => void;
  visible: boolean;
  address?: string | undefined;
  parcelData?: ParcelData | null;
  isLoadingParcel?: boolean;
  parcelError?: string | null;
}

/**
 * Sleek pill button for option selection
 */
const PillButton: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap
      transition-all duration-150 ease-out
      ${selected 
        ? 'bg-teal-500 text-white shadow-md scale-105' 
        : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-700'
      }
    `}
  >
    {children}
  </button>
);

// Helper to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format lot size for display
 * Converts between acres and square feet for readable display
 * - If unit is AC/ACRE/ACRES, display as acres
 * - If unit is SQFT/SF or value > 1 acre in sqft, may convert
 * - Default to showing the value with its unit
 */
const formatLotSize = (area: number | null, unit: string | null): string | null => {
  if (!area || area <= 0) return null;
  
  const normalizedUnit = (unit || '').toUpperCase().trim();
  
  // Handle acres
  if (normalizedUnit.includes('AC') || normalizedUnit === 'ACRE' || normalizedUnit === 'ACRES') {
    if (area < 1) {
      return `${area.toFixed(2)} acres`;
    }
    return `${area.toFixed(2)} acres`;
  }
  
  // Handle square feet
  if (normalizedUnit.includes('SQ') || normalizedUnit === 'SF' || normalizedUnit === 'SQFT') {
    // Convert to acres if larger than 0.5 acre (21,780 sqft)
    if (area > 21780) {
      const acres = area / 43560;
      return `${acres.toFixed(2)} acres`;
    }
    return `${area.toLocaleString()} sq ft`;
  }
  
  // Default: assume acres if small number, sqft if large
  if (area < 100) {
    return `${area.toFixed(2)} acres`;
  }
  return `${area.toLocaleString()} sq ft`;
};

// Helper to find matching value range for a given market value
const findValueRangeForAmount = (amount: number): PropertyValueRange | null => {
  return PROPERTY_VALUE_RANGES.find(
    range => amount >= range.minValue && amount <= range.maxValue
  ) || null;
};

// Helper to find matching age range for a given year built
const findAgeRangeForYear = (yearBuilt: number): HomeAgeRange | null => {
  const currentYear = new Date().getFullYear();
  const age = currentYear - yearBuilt;
  return HOME_AGE_RANGES.find(
    range => age >= range.minYears && age < range.maxYears
  ) || null;
};

export const PropertyDetailsForm: React.FC<PropertyDetailsFormProps> = ({
  onDetailsChange,
  visible,
  address,
  parcelData,
  isLoadingParcel = false,
  parcelError,
}) => {
  const [selectedValue, setSelectedValue] = useState<PropertyValueRange | null>(null);
  const [selectedAge, setSelectedAge] = useState<HomeAgeRange | null>(null);

  // Auto-select based on parcel data when it becomes available
  useEffect(() => {
    if (parcelData) {
      // Auto-select value range based on market value
      if (parcelData.mktValue && !selectedValue) {
        const matchedRange = findValueRangeForAmount(parcelData.mktValue);
        if (matchedRange) {
          setSelectedValue(matchedRange);
        }
      }
      
      // Auto-select age range based on year built
      if (parcelData.yearBuilt && !selectedAge) {
        const matchedRange = findAgeRangeForYear(parcelData.yearBuilt);
        if (matchedRange) {
          setSelectedAge(matchedRange);
        }
      }
    }
  }, [parcelData, selectedValue, selectedAge]);

  useEffect(() => {
    onDetailsChange({
      valueRange: selectedValue,
      ageRange: selectedAge,
    });
  }, [selectedValue, selectedAge, onDetailsChange]);

  useEffect(() => {
    if (!visible) {
      setSelectedValue(null);
      setSelectedAge(null);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div 
      className="w-full max-w-4xl mx-auto animate-fadeIn"
      style={{
        animation: 'fadeIn 0.4s ease-out',
      }}
    >
      {/* Address Badge */}
      {address && (
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {address}
          </span>
        </div>
      )}

      {/* Loading State */}
      {isLoadingParcel && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="w-10 h-10 text-teal-500 animate-spin" />
            <p className="text-gray-600 font-medium">Looking up your property...</p>
            <p className="text-sm text-gray-400">Searching Texas property records</p>
          </div>
        </div>
      )}

      {/* Property Verification Card - Trust-building display */}
      {!isLoadingParcel && parcelData && parcelData.mktValue && (
        <div className="bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 rounded-2xl shadow-xl border-2 border-teal-200 p-6 md:p-8 mb-6 overflow-hidden relative">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.03]" 
               style={{ 
                 backgroundImage: 'radial-gradient(circle, #0d9488 1px, transparent 1px)',
                 backgroundSize: '20px 20px' 
               }} 
          />
          
          {/* Verification Header */}
          <div className="relative flex items-center justify-center gap-3 mb-6 animate-verifySlideIn">
            <div className="flex items-center justify-center w-10 h-10 bg-teal-500 rounded-full shadow-lg animate-verifyPop">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="text-center">
              <span className="text-teal-800 font-bold text-lg uppercase tracking-wider">Property Verified</span>
            </div>
          </div>

          {/* Address Display */}
          <div className="relative text-center mb-6 animate-verifySlideIn" style={{ animationDelay: '100ms' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-lg border border-teal-100">
              <MapPin className="w-4 h-4 text-teal-600" />
              <span className="font-semibold text-gray-800">
                {parcelData.situsAddr}
              </span>
              <span className="text-gray-500">|</span>
              <span className="text-gray-600">
                {parcelData.situsCity}, TX {parcelData.situsZip}
              </span>
              <span className="text-gray-500">|</span>
              <span className="font-medium text-teal-700">
                {parcelData.county} County
              </span>
            </div>
          </div>
          
          {/* Property Info Grid */}
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Market Value - Main Feature */}
            <div className="md:col-span-2 bg-white/90 rounded-xl p-6 text-center border border-teal-100 shadow-sm animate-verifySlideIn" style={{ animationDelay: '200ms' }}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-teal-600" />
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  {parcelData.taxYear ? `${parcelData.taxYear} ` : ''}Appraised Market Value
                </span>
              </div>
              <p className="text-5xl md:text-6xl font-extrabold text-teal-600 tracking-tight">
                {formatCurrency(parcelData.mktValue)}
              </p>
            </div>

            {/* Year Built */}
            {parcelData.yearBuilt && (
              <div className="bg-white/90 rounded-xl p-4 text-center border border-teal-100 shadow-sm animate-verifySlideIn" style={{ animationDelay: '300ms' }}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-teal-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Year Built</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{parcelData.yearBuilt}</p>
                <p className="text-sm text-gray-500 mt-1">{new Date().getFullYear() - parcelData.yearBuilt} years old</p>
              </div>
            )}

            {/* Lot Size */}
            {formatLotSize(parcelData.legalArea, parcelData.legalAreaUnit) && (
              <div className="bg-white/90 rounded-xl p-4 text-center border border-teal-100 shadow-sm animate-verifySlideIn" style={{ animationDelay: '400ms' }}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Ruler className="w-4 h-4 text-teal-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lot Size</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{formatLotSize(parcelData.legalArea, parcelData.legalAreaUnit)}</p>
              </div>
            )}

            {/* Land Value */}
            {parcelData.landValue && (
              <div className="bg-white/90 rounded-xl p-4 text-center border border-teal-100 shadow-sm animate-verifySlideIn" style={{ animationDelay: '500ms' }}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Home className="w-4 h-4 text-teal-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Land Value</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{formatCurrency(parcelData.landValue)}</p>
              </div>
            )}

            {/* Improvement Value */}
            {parcelData.impValue && (
              <div className="bg-white/90 rounded-xl p-4 text-center border border-teal-100 shadow-sm animate-verifySlideIn" style={{ animationDelay: '600ms' }}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Home className="w-4 h-4 text-teal-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Improvement Value</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{formatCurrency(parcelData.impValue)}</p>
              </div>
            )}
          </div>

          {/* Property ID & Source Attribution */}
          <div className="relative mt-6 pt-4 border-t border-teal-200/50 animate-verifySlideIn" style={{ animationDelay: '700ms' }}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
              {/* Property ID */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 rounded-lg">
                <FileText className="w-4 h-4 text-teal-600" />
                <span className="text-gray-500">Property ID:</span>
                <span className="font-mono font-semibold text-gray-700">{parcelData.propId}</span>
              </div>
              
              {/* Source */}
              <div className="text-gray-400 text-xs">
                Source: {parcelData.county} County Appraisal District{parcelData.taxYear ? ` ${parcelData.taxYear}` : ''}
              </div>
            </div>
          </div>

          <p className="relative text-sm text-center text-teal-700 mt-4 font-medium animate-verifySlideIn" style={{ animationDelay: '800ms' }}>
            ✨ We'll use this official data to calculate your potential savings
          </p>
        </div>
      )}

      {/* Error message */}
      {!isLoadingParcel && parcelError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-center">
          <p className="text-amber-700 text-sm">{parcelError}</p>
        </div>
      )}

      {/* Two-Row Selection Form - Show when not loading */}
      {!isLoadingParcel && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 space-y-6">
          
          {/* Row 1: Home Value */}
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-3 text-center">
              {parcelData?.mktValue ? 'Confirm your home value range:' : "What's your home worth?"}
            </label>
            <div className="flex flex-wrap justify-center gap-2">
              {PROPERTY_VALUE_RANGES.map((range) => (
                <PillButton
                  key={range.id}
                  selected={selectedValue?.id === range.id}
                  onClick={() => setSelectedValue(range)}
                >
                  {range.label}
                </PillButton>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Row 2: Home Age */}
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-3 text-center">
              {parcelData?.yearBuilt ? 'Confirm your home age:' : 'How old is your home?'}
            </label>
            <div className="flex flex-wrap justify-center gap-2">
              {HOME_AGE_RANGES.map((range) => (
                <PillButton
                  key={range.id}
                  selected={selectedAge?.id === range.id}
                  onClick={() => setSelectedAge(range)}
                >
                  {range.label}
                </PillButton>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes verifySlideIn {
          from { 
            opacity: 0; 
            transform: translateY(15px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes verifyPop {
          0% { 
            opacity: 0;
            transform: scale(0.5); 
          }
          50% { 
            transform: scale(1.15); 
          }
          100% { 
            opacity: 1;
            transform: scale(1); 
          }
        }
        
        .animate-verifySlideIn {
          animation: verifySlideIn 0.5s ease-out forwards;
          opacity: 0;
        }
        
        .animate-verifyPop {
          animation: verifyPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
      `}</style>
    </div>
  );
};

export default PropertyDetailsForm;

